import {CommonModule} from '@angular/common';
import {Component, DestroyRef, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSelectModule} from '@angular/material/select';
import {Router} from '@angular/router';
import {filter, finalize} from 'rxjs';
import {
  CONNECT_PROVIDER_FREE_FIELDS,
  ConnectFunctionDefinition,
  ConnectGuidedWorkflowSummary,
  ConnectNoviceLocalService,
  LocalConnectRun,
} from './connect-novice-local.service';
import {ConnectProviderFreeFieldKey} from '../../../services/connect/connect-workflows.service';
import {ClientStateService} from '../../../services/auth/client-state.service';

@Component({
  selector: 'app-connect-novice-local',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
  ],
  templateUrl: './connect-novice-local.component.html',
  styleUrl: './connect-novice-local.component.scss',
})
export class ConnectNoviceLocalComponent {
  private readonly localConnect = inject(ConnectNoviceLocalService);
  private readonly router = inject(Router);
  private readonly clientState = inject(ClientStateService);
  private readonly destroyRef = inject(DestroyRef);
  private clientGeneration = 0;

  functions: ConnectFunctionDefinition[] = [];
  guidedWorkflows: ConnectGuidedWorkflowSummary[] = [];
  selected: ConnectFunctionDefinition | null = null;
  loading = true;
  loadError = false;
  step = 1;
  running = false;
  result: 'success' | 'error' | null = null;
  resultTitle = '';
  searchQuery = '';
  creating = false;
  saving = false;
  saveError = false;
  createFieldError = false;
  readonly availableFields = CONNECT_PROVIDER_FREE_FIELDS;
  readonly selectedCreateFields = new Set<ConnectProviderFreeFieldKey>(['full_name', 'work_email', 'company']);

  form = new FormGroup<Record<string, FormControl<string>>>({});
  createForm = new FormGroup({
    name: new FormControl('', {nonNullable: true, validators: [Validators.required, Validators.maxLength(80)]}),
  });

  get visibleFunctions(): ConnectFunctionDefinition[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return this.functions;
    return this.functions.filter(item =>
      `${item.title} ${item.description}`.toLowerCase().includes(query),
    );
  }

  get visibleGuidedWorkflows(): ConnectGuidedWorkflowSummary[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return this.guidedWorkflows;
    return this.guidedWorkflows.filter(item => item.title.toLowerCase().includes(query));
  }

  constructor() {
    this.clientState.resetCache$.pipe(
      filter(reset => reset),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.reloadForClient());
    this.reloadForClient();
  }

  openAdvancedWorkflows(): void {
    void this.router.navigate(['/connect/workflows/advanced']);
  }

  private reloadForClient(): void {
    const generation = ++this.clientGeneration;
    this.clearClientState();
    this.localConnect.loadFunctions().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: functions => {
        if (generation !== this.clientGeneration) return;
        this.functions = functions;
        this.loading = false;
      },
      error: () => {
        if (generation !== this.clientGeneration) return;
        this.loading = false;
        this.loadError = true;
      },
    });
    this.localConnect.loadGuidedWorkflows().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: workflows => {
        if (generation === this.clientGeneration) this.guidedWorkflows = workflows;
      },
      error: () => {
        if (generation === this.clientGeneration) this.loadError = true;
      },
    });
  }

  private clearClientState(): void {
    this.functions = [];
    this.guidedWorkflows = [];
    this.selected = null;
    this.form = new FormGroup<Record<string, FormControl<string>>>({});
    this.loading = true;
    this.loadError = false;
    this.step = 1;
    this.running = false;
    this.result = null;
    this.resultTitle = '';
    this.searchQuery = '';
    this.creating = false;
    this.saving = false;
    this.saveError = false;
    this.createFieldError = false;
  }

  createWorkflow(): void {
    void this.router.navigate(['/connect/workflows/new']);
  }

  openWorkflow(workflow: ConnectGuidedWorkflowSummary): void {
    void this.router.navigate(['/connect/workflows', workflow.workflowId, 'guided']);
  }

  choose(definition: ConnectFunctionDefinition): void {
    this.selected = definition;
    const controls: Record<string, FormControl<string>> = {};
    definition.fields.forEach(field => {
      const validators = field.required ? [Validators.required] : [];
      if (field.format === 'email') validators.push(Validators.email);
      controls[field.key] = new FormControl(field.defaultValue, {nonNullable: true, validators});
    });
    this.form = new FormGroup(controls);
    this.step = 2;
    this.result = null;
  }

  updateSearch(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value;
  }

  startCreating(): void {
    this.creating = true;
    this.saveError = false;
    this.createFieldError = false;
    this.createForm.reset({name: ''});
    this.selectedCreateFields.clear();
    ['full_name', 'work_email', 'company'].forEach(key => this.selectedCreateFields.add(key as ConnectProviderFreeFieldKey));
  }

  cancelCreating(): void {
    this.creating = false;
    this.saveError = false;
  }

  toggleCreateField(key: ConnectProviderFreeFieldKey, checked: boolean): void {
    if (checked) {
      this.selectedCreateFields.add(key);
    } else {
      this.selectedCreateFields.delete(key);
    }
    this.createFieldError = false;
  }

  saveFunction(): void {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }
    if (!this.selectedCreateFields.size) {
      this.createFieldError = true;
      return;
    }

    const generation = this.clientGeneration;
    this.saving = true;
    this.saveError = false;
    this.localConnect.createFunction(
      this.createForm.controls.name.value,
      this.availableFields
        .map(field => field.key)
        .filter(key => this.selectedCreateFields.has(key)),
    ).pipe(finalize(() => {
      if (generation === this.clientGeneration) this.saving = false;
    })).subscribe({
      next: definition => {
        if (generation !== this.clientGeneration) return;
        this.functions = [...this.functions, definition]
          .sort((left, right) => left.title.localeCompare(right.title));
        this.creating = false;
        this.choose(definition);
      },
      error: () => {
        if (generation === this.clientGeneration) this.saveError = true;
      },
    });
  }

  back(): void {
    this.step = Math.max(1, this.step - 1);
    this.result = null;
  }

  review(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.step = 3;
  }

  runSafely(): void {
    if (!this.selected) return;
    const generation = this.clientGeneration;
    this.running = true;
    this.result = null;
    this.localConnect.run(this.selected, this.inputForSelected()).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        if (generation === this.clientGeneration) this.running = false;
      }),
    ).subscribe({
      next: run => {
        if (generation === this.clientGeneration) this.readTerminalRun(run);
      },
      error: () => {
        if (generation !== this.clientGeneration) return;
        this.result = 'error';
        this.resultTitle = 'Connect could not complete the test.';
      },
      complete: () => {
        if (generation !== this.clientGeneration) return;
        if (this.result) return;
        this.result = 'error';
        this.resultTitle = 'Connect stopped waiting for this test.';
      },
    });
  }

  startOver(): void {
    this.step = 1;
    this.result = null;
  }

  private inputForSelected(): Record<string, unknown> {
    return this.form.getRawValue();
  }

  private readTerminalRun(run: LocalConnectRun): void {
    if (!['succeeded', 'failed', 'cancelled', 'timed_out'].includes(run.status)) return;
    this.result = run.status === 'succeeded' ? 'success' : 'error';
    this.resultTitle = run.status === 'succeeded'
      ? `${this.selected?.title ?? 'The function'} works.`
      : 'Connect stopped the test safely.';
  }
}
